import uniq from 'lodash/uniq'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { isPending, trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { Alert, exportDataToCSV, formatDate } from '@avantstay/backoffice-core'

import { QuotesFormView } from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/CreateQuoteForm.types'
import { CreateQuoteFormStore } from '@new/domains/bookingsTapeChart/stores/CreateQuoteFormStore'
import { quotesExportableFields } from '@new/domains/marketing/screens/Quotes/definitions'

import { getPreBookingPriceTotal } from '@arriere/modules/bookingPrices'
import { getHomeDataToExportQuotes } from '@arriere/modules/quotes'
import { PreBookingPriceQuote } from '@arriere/types/BookingPrices.types'
import { trackError } from '@tracking/customEventReport'

import { BaseStore } from '../__types'
import { HomeFilterIds } from '../config/filtersConfig'
import { tapeChartCreateTapeStore } from '../internal'
import { TapeChartStateStore } from './TapeChartStateStore'

export class TapeChartQuotesStore {
  // Observables
  listingsForQuotesPreBookingPrice?: PreBookingPriceQuote[]

  constructor(
    private getApiStore: () => BaseStore['apiStore'],
    private getFilterStore: () => BaseStore['filterStore'],
    private getStateStore: () => Partial<TapeChartStateStore>,
  ) {
    makeObservable(this, {
      listingsForQuotesPreBookingPrice: observable,
      selectionQuotesListingIds: computed,
      canCreateQuote: computed,
      hasMultipleListingsSelected: computed,
      shouldDisplayQuotesHeader: computed,
      setPreBookingPriceArray: action.bound,
      deselectHomeForQuotes: action.bound,
      showQuotesModal: action.bound,
    })

    this.exportPropertyInfoCSV = trackedAction<any>(this.exportPropertyInfoCSV.bind(this))
    this.getListingData = trackedAction<any>(this.getListingData.bind(this))

    reaction(
      () => this.filterStore.availableDates,
      () => this.getNewData(),
    )
    reaction(
      () => !!this.filterStore.availableDates && this.selectionQuotesListingIds.length === 0,
      () => this.filterStore.setAvailableDates?.(undefined),
    )
  }

  get apiStore() {
    return this.getApiStore()
  }

  get filterStore() {
    return this.getFilterStore()
  }

  get stateStore() {
    return this.getStateStore()
  }

  get selectionQuotesListingIds(): string[] {
    const { createTapeContext } = tapeChartCreateTapeStore

    const hasIds = createTapeContext?.some(tapeContext => !!tapeContext.home?.id)

    if (hasIds) {
      return createTapeContext?.map(tapeContext => tapeContext.home?.id) as string[]
    }

    return []
  }

  // Computed values
  get canCreateQuote() {
    return !!this.selectionQuotesListingIds?.length && this.selectionQuotesListingIds?.length >= 1
  }

  get hasMultipleListingsSelected() {
    return !!this.selectionQuotesListingIds?.length && this.selectionQuotesListingIds?.length > 1
  }

  get shouldDisplayQuotesHeader() {
    return (
      this.canCreateQuote && CreateQuoteFormStore.quotesFormView === QuotesFormView.emailPreview
    )
  }

  getSelectedListingsForQuotesState = computedFn(
    () => this.selectionQuotesListingIds && this.selectionQuotesListingIds.length > 0,
  )

  getTotalPriceForSelectedQuote = computedFn((homeId: string) => {
    return this.listingsForQuotesPreBookingPrice?.find(homePrice => homePrice.homeId === homeId)
  })

  getSelectedListingForQuote = computedFn((listingId: string) => {
    return !!this.selectionQuotesListingIds?.find(selectedId => selectedId === listingId)
  })

  isLoadingListingData = computedFn((listingId: string) => {
    const alreadyHasData = this.listingsForQuotesPreBookingPrice?.some(price =>
      price.homeId?.includes(listingId),
    )

    return !alreadyHasData && isPending(this.getListingData)
  })

  getNewData = computedFn(() => {
    uniq(this.selectionQuotesListingIds)?.map(
      listingId => listingId && this.getListingData(listingId),
    )
  })

  getMinStay = computedFn((homeId: string, fromDateRange?: string) => {
    if (fromDateRange && this.stateStore.selectRangeProps?.datePrices) {
      const rangeStart = formatDate(fromDateRange)

      const minStay = this.stateStore.selectRangeProps?.datePrices.find(
        datePrice => formatDate(datePrice.date) === rangeStart && datePrice.homeId === homeId,
      )?.minStay

      return minStay || undefined
    }
    return undefined
  })

  private dataIsAlreadyAvailable(homeId: string, fromDate?: string, toDate?: string): boolean {
    return !!this.listingsForQuotesPreBookingPrice?.find(
      price =>
        price.homeId?.includes(homeId) &&
        fromDate === price.range.fromDate &&
        toDate === price.range.toDate,
    )
  }

  private prepareHomeData() {
    const { availableDates } = this.filterStore
    const { createTapeContext } = tapeChartCreateTapeStore

    // When quotes tool is not active, but we select range, it should use context period
    const tapeContextDates =
      createTapeContext && createTapeContext?.length <= 1
        ? createTapeContext?.[0]?.period
        : undefined

    if (!this.selectionQuotesListingIds && (!availableDates || !tapeContextDates)) {
      return undefined
    }

    const fromDate = availableDates ? formatDate(availableDates?.start) : tapeContextDates?.fromDate
    const toDate = availableDates ? formatDate(availableDates?.end) : tapeContextDates?.toDate

    return { fromDate, toDate }
  }

  private insertPreBookingPrice(quote: PreBookingPriceQuote) {
    this.listingsForQuotesPreBookingPrice = [
      ...(this.listingsForQuotesPreBookingPrice || []),
      quote,
    ]
  }

  private updatePreBookingPrice(quote: PreBookingPriceQuote, index: number) {
    if (!this.listingsForQuotesPreBookingPrice) return
    this.listingsForQuotesPreBookingPrice[index] = quote
  }

  private async getPrebookedPriceData(
    homeId: string,
    fromDate: string,
    toDate: string,
    minStay?: number,
  ) {
    const { filteringArgs } = this.filterStore

    const guestsFilter = filteringArgs?.[HomeFilterIds.Guests]
    const petsFilter = filteringArgs?.[HomeFilterIds.MinPets]

    return await getPreBookingPriceTotal({
      homeId: homeId,
      from: fromDate,
      until: toDate,
      guests: guestsFilter?.from || 1,
      pets: petsFilter || 0,
    }).then(response => {
      return {
        homeId,
        minStay,
        range: { fromDate, toDate },
        total: response?.total.total,
        subtotal: response?.total.subtotal,
        deposits: response?.total.deposits,
      }
    })
  }

  private addPriceData(priceData: PreBookingPriceQuote) {
    this.setPreBookingPriceArray(priceData)
  }

  // Actions

  async getListingData(homeId?: string) {
    const dateRange = this.prepareHomeData()

    if (
      !homeId ||
      !dateRange?.fromDate ||
      !dateRange?.toDate ||
      this.dataIsAlreadyAvailable(homeId, dateRange.fromDate, dateRange.toDate)
    ) {
      return Promise.resolve(undefined)
    }

    const minStay = this.getMinStay(homeId, dateRange.fromDate)

    const priceData = await this.getPrebookedPriceData(
      homeId,
      dateRange?.fromDate,
      dateRange?.toDate,
      minStay,
    )

    this.addPriceData(priceData)
  }

  setPreBookingPriceArray(quote: PreBookingPriceQuote) {
    const index = this.listingsForQuotesPreBookingPrice?.findIndex(
      home => home.homeId === quote.homeId,
    )
    if (index !== undefined && index !== -1) {
      this.updatePreBookingPrice(quote, index)
    } else {
      this.insertPreBookingPrice(quote)
    }
  }

  deselectHomeForQuotes() {
    if (!this.selectionQuotesListingIds) {
      return
    }

    tapeChartCreateTapeStore.createTapeContext = undefined
    this.filterStore.setAvailableDates?.(undefined)
  }

  showQuotesModal() {
    if (!this.selectionQuotesListingIds) {
      return
    }
  }

  async exportPropertyInfoCSV() {
    const listingIds = this.selectionQuotesListingIds || []
    const { availableDates } = this.filterStore

    try {
      const homesData = await getHomeDataToExportQuotes(listingIds)

      const data = homesData.map(home => ({
        id: home.id,
        name: home.name,
        address: home.address.address,
        city: home.address.city,
        zipCode: home.address.zipCode,
        region: home.region?.name,
        bedroomsCount: home.bedroomsCount,
        bathroomsCount: home.bathroomsCount,
        halfBathroomsCount: home.halfBathroomsCount,
        maxOccupancy: home.maxOccupancy,
        petPolicy: home.petPolicy,
      }))

      exportDataToCSV(data, quotesExportableFields, {
        filename: `${availableDates?.start}_${availableDates?.end}-quotes`,
      })

      Alert.Success('CSV file created successfully')
    } catch (error) {
      const errorMessage = `Export Quotes CSV:`
      trackError(errorMessage, { error })
      Alert.Error(`${errorMessage} ${error}`)
    }
  }
}
